import React, { useMemo, useState } from 'react'
import { Avatar, Bar, FoBtn, KV, Pill, Ring, Section, Sheet } from './fieldUi'
import { LinesTable, RETURN_LABEL, RETURN_TONE } from './shared'
import { useField } from './fieldContext'
import { pendingCount, storeInfo, visitStats, visitsIn, goalOf, salesOf, paceFraction } from '../store'
import { monthKey, monthLabelOf, money, money2, pct, todayIso, fmtStamp, lb } from '../FieldOpsKit'

/** Me: my month (compliance, goals, orders / deliveries), the stores that need me, my returns and the sync status. */
export default function MeScreen() {
    const f = useField()
    const { state, actor, nav } = f
    const isDriver = actor.kind === 'driver'
    const today = todayIso()
    const month = monthKey(today)
    const [ret, setRet] = useState(null)
    const myReturns = state.field.returns.filter((r) => r.actor === actor.key)
    const days = Object.keys(state.field.days).filter((k) => k.indexOf(actor.key + '|') === 0 && k.split('|')[1].slice(0, 7) === month).map((k) => state.field.days[k])
    const miles = days.reduce((a, d) => a + (d.miles || 0), 0)

    // ---- salesperson numbers ----
    const rep = useMemo(() => {
        if (isDriver) return null
        const visits = visitsIn(state.sales, month, (v) => v.rep === actor.rep && v.date <= today)
        const st = visitStats(visits)
        const due = st.done + st.missed
        const mine = state.sales.stores.filter((s) => s.rep === actor.rep)
        let goal = 0; let sold = 0
        mine.forEach((s) => { const g = goalOf(state.sales, month, s.id, true); if (g) { goal += g; sold += salesOf(state.sales, month, s.id) } })
        const done = visits.filter((v) => v.status === 'done')
        const orders = done.filter((v) => v.outcome === 'order')
        const gaps = mine.map((s) => ({ s: s, info: storeInfo(state.sales, s.id, f.vIdx, f.oIdx) })).filter((x) => x.info.state.key === 'behind').sort((a, b) => b.info.gap - a.info.gap).slice(0, 5)
        const avgDur = done.length ? Math.round(done.reduce((a, v) => a + (v.dur || 0), 0) / done.length) : 0
        return { st: st, due: due, goal: goal, sold: sold, done: done.length, orders: orders.length, gaps: gaps, avgDur: avgDur, noOrder: done.filter((v) => v.outcome === 'no_order').length, closed: done.filter((v) => v.outcome === 'closed').length }
    }, [state.sales, actor.key]) // eslint-disable-line react-hooks/exhaustive-deps

    // ---- driver numbers ----
    const drv = useMemo(() => {
        if (!isDriver) return null
        const list = state.drivers.deliveries.filter((d) => d.driverId === actor.driverId && monthKey(d.date) === month && d.date <= today && d.status !== 'cancelled')
        const done = list.filter((d) => d.status === 'delivered')
        const onTime = done.filter((d) => d.deliveredOn <= d.date).length
        return { total: list.length, done: done.length, onTime: onTime, failed: list.filter((d) => d.events.some((e) => e.type === 'failed')).length, partial: done.filter((d) => d.shortLines && d.shortLines.length).length, cod: done.reduce((a, d) => a + (d.payment ? d.payment.amount : 0), 0), weight: done.reduce((a, d) => a + d.weightLb, 0) }
    }, [state.drivers, actor.key]) // eslint-disable-line react-hooks/exhaustive-deps

    const pending = pendingCount(state)

    return (
        <div className="fo-stack">
            <div className="fo-card fo-me__head">
                <Avatar name={actor.name} tone={isDriver ? 'green' : 'blue'} />
                <div><strong>{actor.name}</strong><span>{isDriver ? 'Driver' : 'Salesperson ' + actor.rep} · {monthLabelOf(month)}</span></div>
            </div>

            {rep ? (
                <React.Fragment>
                    <div className="fo-hero fo-hero--me">
                        <div className="fo-hero__stats">
                            <Ring value={rep.st.done} max={Math.max(1, rep.due)} label={pct(rep.st.done, rep.due) + '%'} sub="compliance" size={112} stroke={10} />
                            <div className="fo-hero__kpis">
                                <div><span>Visits done</span><strong>{rep.st.done}/{rep.due}</strong></div>
                                <div><span>Missed</span><strong>{rep.st.missed}</strong></div>
                                <div><span>With order</span><strong>{pct(rep.orders, rep.done)}%</strong></div>
                                <div><span>Avg. time in store</span><strong>{rep.avgDur} min</strong></div>
                            </div>
                        </div>
                    </div>
                    <Section title="My goals this month">
                        <div className="fo-goal"><div className="fo-goal__top"><span>All my stores</span><strong>{money(rep.sold)} <small>of {money(rep.goal)}</small></strong></div><Bar value={rep.sold} max={rep.goal} tone={rep.sold >= rep.goal ? 'green' : pct(rep.sold, rep.goal) >= 70 ? 'blue' : 'amber'} /><div className="fo-goal__hint">{pct(rep.sold, rep.goal)}% of the goal · {rep.goal > rep.sold ? money(rep.goal - rep.sold) + ' to go' : 'goal reached'} · the month is {Math.round(paceFraction(month) * 100)}% done</div></div>
                        <KV label="Orders taken">{rep.orders}</KV>
                        <KV label="Visits without order">{rep.noOrder} · {rep.closed} store closed</KV>
                        <KV label="Driving this month">{miles ? miles + ' mi' : '—'}</KV>
                    </Section>
                    <Section title="Stores that need you" right={<FoBtn kind="soft" small onClick={() => nav.go('stores')}>All stores</FoBtn>}>
                        {rep.gaps.length === 0 ? <div className="fo-muted">Nothing behind goal. Great job.</div> : rep.gaps.map((g) => (
                            <button key={g.s.id} type="button" className="fo-visitrow" style={{ marginBottom: 8 }} onClick={() => nav.push({ type: 'store', storeId: g.s.id })}>
                                <div><strong>{g.s.name}</strong><span>{g.s.city} · {g.info.daysSince !== null ? g.info.daysSince + ' days since last visit' : 'never visited'}</span></div>
                                <div className="fo-visitrow__r"><strong>{money(g.info.gap)}</strong><small>to goal</small></div>
                            </button>
                        ))}
                    </Section>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <div className="fo-hero fo-hero--me">
                        <div className="fo-hero__stats">
                            <Ring value={drv.onTime} max={Math.max(1, drv.done)} label={pct(drv.onTime, drv.done) + '%'} sub="on time" size={104} stroke={10} />
                            <div className="fo-hero__kpis">
                                <div><span>Delivered</span><strong>{drv.done}/{drv.total}</strong></div>
                                <div><span>Failed attempts</span><strong>{drv.failed}</strong></div>
                                <div><span>Partial</span><strong>{drv.partial}</strong></div>
                                <div><span>Weight moved</span><strong>{lb(drv.weight)}</strong></div>
                            </div>
                        </div>
                    </div>
                    <Section title="This month">
                        <KV label="Cash / checks collected">{money2(drv.cod)}</KV>
                        <KV label="Driving">{miles ? miles + ' mi' : '—'}</KV>
                        <KV label="Returns requested">{myReturns.length}</KV>
                    </Section>
                </React.Fragment>
            )}

            <Section title="My returns" right={<Pill tone="gray">{myReturns.length}</Pill>}>
                {myReturns.length === 0 ? <div className="fo-muted">No returns yet.</div> : myReturns.map((r) => (
                    <button key={r.id} type="button" className="fo-visitrow" style={{ marginBottom: 8 }} onClick={() => setRet(r)}>
                        <div><strong>{r.no} · {money2(r.total)}</strong><span>{r.lines.map((l) => l.qty + '× ' + l.name).join(', ')}</span></div>
                        <div className="fo-visitrow__r"><Pill tone={RETURN_TONE[r.status]}>{RETURN_LABEL[r.status]}</Pill>{r.pending ? <Pill tone="amber">To send</Pill> : null}</div>
                    </button>
                ))}
            </Section>

            <Section title="This phone">
                <KV label="Connection">{state.field.online ? <Pill tone="green">Online</Pill> : <Pill tone="amber">Offline</Pill>}</KV>
                <KV label="Waiting to be sent">{pending ? <Pill tone="amber">{pending} item{pending === 1 ? '' : 's'}</Pill> : <Pill tone="green">All sent</Pill>}</KV>
            </Section>

            <Sheet show={!!ret} onClose={() => setRet(null)} title={ret ? ret.no : ''} subtitle={ret ? ret.customer || '' : ''} tall footer={<FoBtn kind="primary" onClick={() => setRet(null)}>Close</FoBtn>}>
                {ret ? (
                    <React.Fragment>
                        <div style={{ marginBottom: 10 }}><Pill tone={RETURN_TONE[ret.status]}>{RETURN_LABEL[ret.status]}</Pill></div>
                        <LinesTable lines={ret.lines.map((l) => ({ sku: l.sku, name: l.name + ' — ' + l.reason, qty: l.qty, price: l.price }))} />
                        {ret.notes ? <div className="fo-note">{ret.notes}</div> : null}
                        <h4 className="fo-sheet__h4">Timeline</h4>
                        {ret.history.map((h, i) => <KV key={i} label={fmtStamp(h.ts)}>{h.type.replace('_', ' ')} · {h.by}{h.note ? ' — ' + h.note : ''}</KV>)}
                    </React.Fragment>
                ) : null}
            </Sheet>
        </div>
    )
}
